import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Modal from '../../../../UI/components/UIKit/Modal'
import { MAIN_COLORS } from '../../../../UI/consts/colors'
import { IStorie } from '../../../../components/types/stories'
import { API } from '../../../../components/API'
import Skeleton from '../../../../UI/components/UIKit/Skeleton'
import { getTelegramAppLink } from '../../../../UI/consts/telegram'
import rehypeRaw from 'rehype-raw'

const ReactMarkdown = lazy(() => import('react-markdown'))

const MarkdownLink = ({ href, children, ...props }: any) => {
    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        if (!href) return
        try {
            const url = new URL(href, window.location.origin)
            if (url.protocol === 'http:' || url.protocol === 'https:') {
                window.Telegram.WebApp.openLink(href, { try_instant_view: true })
            }
        } catch {}
    }
    const safeHref = (() => {
        if (!href) return undefined
        try {
            const url = new URL(href, window.location.origin)
            return (url.protocol === 'http:' || url.protocol === 'https:') ? href : undefined
        } catch { return undefined }
    })()
    return <a {...props} href={safeHref} onClick={handleClick}>{children}</a>
}

const markdownComponents = {
    a: MarkdownLink,
}

const MarkdownContent = styled.div`
    padding: 0 16px 24px;
    color: #ccc;
    font-size: 15px;
    line-height: 1.7;

    h1, h2, h3 {
        color: white;
        margin: 20px 0 10px;
    }
    h1 { font-size: 28px; }
    h2 { font-size: 18px; }
    h3 { font-size: 16px; }

    p { margin-bottom: 14px; }

    strong { color: white; }

    img {
        max-width: 100%;
        border-radius: 12px;
        margin: 12px 0;
    }

    a { color: #FFD700; }

    ul, ol {
        padding-left: 20px;
        margin-bottom: 14px;
    }

    blockquote {
        border-left: 3px solid #FFD700;
        padding: 8px 16px;
        margin: 12px 0;
        background: rgba(255, 215, 0, 0.1);
        border-radius: 0 8px 8px 0;
    }
`

const Title = styled.h1`
    color: white;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
    padding: 0 16px 16px;
`

const CoverImage = styled.img`
    width: 100%;
    height: 220px;
    object-fit: cover;
    margin-bottom: 16px;
    border-radius: 0 0 16px 16px;
`

const CoverFallback = styled.div`
    width: 100%;
    height: 120px;
    background: linear-gradient(135deg, ${MAIN_COLORS.purple_300} 0%, ${MAIN_COLORS.purple_400} 100%);
    margin-bottom: 16px;
`

const Category = styled.span`
    display: inline-block;
    padding: 4px 10px;
    background: rgba(255, 215, 0, 0.2);
    color: #FFD700;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    margin: 0 16px 12px;
`

const ErrorMessage = styled.div`
    padding: 32px 16px;
    text-align: center;
    color: #888;
    font-size: 15px;
    line-height: 1.6;
`

const FallbackLink = styled.a`
    display: block;
    text-align: center;
    padding: 12px;
    margin-top: 16px;
    color: #FFD700;
    font-size: 14px;
`

const ShareButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: calc(100% - 32px);
    margin: 8px 16px 24px;
    padding: 14px;
    background: ${MAIN_COLORS.gradient_yellow_pink};
    border: none;
    border-radius: 12px;
    color: #1a1a2e;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
`

const StoryViewer = ({ story, isOpen, onClose }: {
    story: IStorie | null,
    isOpen: boolean,
    onClose: () => void
}) => {
    const [markdown, setMarkdown] = useState<string | null>(null)
    const [coverImage, setCoverImage] = useState<string>('')
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const requestIdRef = useRef(0)

    useEffect(() => {
        if (isOpen) {
            window.Telegram.WebApp.BackButton.show()
            window.Telegram.WebApp.BackButton.onClick(onClose)
        } else {
            window.Telegram.WebApp.BackButton.hide()
            window.Telegram.WebApp.BackButton.offClick(onClose)
        }
        return () => {
            window.Telegram.WebApp.BackButton.hide()
            window.Telegram.WebApp.BackButton.offClick(onClose)
        }
    }, [isOpen, onClose])

    useEffect(() => {
        if (isOpen && story) {
            const currentRequest = ++requestIdRef.current
            setIsLoading(true)
            setMarkdown(null)
            setHasError(false)
            setCoverImage('')

            Promise.all([
                API.getStory(story.id).then(res => {
                    if (currentRequest !== requestIdRef.current) return
                    setMarkdown(res?.story?.body_markdown || '')
                }).catch(() => {
                    if (currentRequest !== requestIdRef.current) return
                    setHasError(true)
                }),
                API.getImageData(story.id, 'stories').then(res => {
                    if (currentRequest !== requestIdRef.current) return
                    setCoverImage(res)
                }).catch(() => {})
            ]).finally(() => {
                if (currentRequest !== requestIdRef.current) return
                setIsLoading(false)
            })
        }
    }, [isOpen, story?.id])

    const handleFallback = useCallback(() => {
        if (story?.external_url) {
            window.Telegram.WebApp.openLink(story.external_url, { try_instant_view: true })
        }
    }, [story?.external_url])

    const handleShare = useCallback(() => {
        if (!story) return
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success')
        const appLink = getTelegramAppLink(`story${story.id}`)
        const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(appLink)}`
        try {
            window.Telegram.WebApp.openTelegramLink(shareUrl)
        } catch (error) {
            console.error('Error sharing story:', error)
        }
    }, [story?.id])

    return (
        <Modal fullscreen isOpen={isOpen} close={onClose} closable={false}>
            {isLoading ? (
                <div style={{ padding: '16px' }}>
                    <Skeleton height={'200px'} width={'100%'} radius={'12px'} />
                    <div style={{ marginTop: '16px' }}>
                        <Skeleton height={'28px'} width={'70%'} radius={'8px'} />
                    </div>
                    <div style={{ marginTop: '12px' }}>
                        <Skeleton height={'16px'} width={'100%'} radius={'4px'} />
                    </div>
                </div>
            ) : hasError ? (
                <ErrorMessage>
                    Не удалось загрузить статью.<br/>
                    Проверьте подключение к интернету и попробуйте снова.
                    {story?.external_url && (
                        <FallbackLink onClick={handleFallback}>
                            Открыть во внешнем браузере
                        </FallbackLink>
                    )}
                </ErrorMessage>
            ) : (
                <>
                    {coverImage
                        ? <CoverImage src={coverImage} alt={story?.title || ''} />
                        : <CoverFallback />
                    }
                    <MarkdownContent>
                        <Suspense fallback={<Skeleton height={'100px'} width={'100%'} radius={'8px'} />}>
                            <ReactMarkdown components={markdownComponents} rehypePlugins={[rehypeRaw]}>{markdown || ''}</ReactMarkdown>
                        </Suspense>
                    </MarkdownContent>
                    <ShareButton onClick={handleShare}>
                        Поделиться
                    </ShareButton>
                </>
            )}
        </Modal>
    )
}

export default StoryViewer
